import { useState } from 'react';
import {
  Modal,
  Progress,
  Table,
  Tag,
  Typography,
  Descriptions,
  Divider,
} from 'antd';
import type { DesgloseReglas } from '../api';
import { pctColor } from '../utils/format';

const { Title, Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  rfc: string;
  periodo?: string;
  porcentaje: number;
  desglose?: DesgloseReglas;
}

interface FilaDesglose {
  key: string;
  categoria: string;
  cantidad: number;
  descripcion: string;
  color: string;
}

const REGLAS_SCORING = [
  { regla: 'Clave de rastreo SPEI exacta', puntos: 100, tipo: 'directo' },
  { regla: 'RFC del tercero coincide', puntos: 40, tipo: 'scoring' },
  { regla: 'Monto exacto', puntos: 35, tipo: 'scoring' },
  { regla: 'Monto similar (diferencia <= 1%)', puntos: 25, tipo: 'scoring' },
  { regla: 'Fecha exacta', puntos: 20, tipo: 'scoring' },
  { regla: 'Fecha cercana (método PUE, hasta fin de mes)', puntos: '20 decae', tipo: 'scoring' },
  { regla: 'Fecha cercana (otros, 3 días)', puntos: '20 decae', tipo: 'scoring' },
  { regla: 'Moneda MXN', puntos: 5, tipo: 'scoring' },
  { regla: 'CFDI fuera de período (informativo, no resta)', puntos: 0, tipo: 'informativo' },
];

export function ReglasConciliacionModal({
  visible,
  onClose,
  rfc,
  periodo,
  porcentaje,
  desglose,
}: Props) {
  const [activeTab, setActiveTab] = useState<'desglose' | 'reglas'>('desglose');

  const d = desglose || {
    spei: 0,
    scoring: 0,
    ia: 0,
    manual: 0,
    sin_cfdi: 0,
    sin_mov_banco: 0,
  };

  const total = d.spei + d.scoring + d.ia + d.manual + d.sin_cfdi + d.sin_mov_banco;

  const filas: FilaDesglose[] = [
    {
      key: 'spei',
      categoria: 'Clave SPEI exacta',
      cantidad: d.spei,
      descripcion: 'Match directo por clave de rastreo (100 pts)',
      color: '#1890ff',
    },
    {
      key: 'scoring',
      categoria: 'Scoring automático',
      cantidad: d.scoring,
      descripcion: 'Conciliación por heurística (>= 80 pts)',
      color: '#52c41a',
    },
    {
      key: 'ia',
      categoria: 'Match por IA',
      cantidad: d.ia,
      descripcion: 'Segunda pasada Nova Lite',
      color: '#722ed1',
    },
    {
      key: 'manual',
      categoria: 'Match manual',
      cantidad: d.manual,
      descripcion: 'Conciliado manualmente por usuario',
      color: '#fa8c16',
    },
    {
      key: 'sin_cfdi',
      categoria: 'Sin CFDI',
      cantidad: d.sin_cfdi,
      descripcion: 'Movimientos bancarios sin factura asociada',
      color: '#fadb14',
    },
    {
      key: 'sin_mov_banco',
      categoria: 'Sin movimiento bancario',
      cantidad: d.sin_mov_banco,
      descripcion: 'CFDIs sin operación bancaria correspondiente',
      color: '#bfbfbf',
    },
  ];

  const columns = [
    {
      title: 'Categoria',
      dataIndex: 'categoria',
      key: 'categoria',
      render: (_: string, record: FilaDesglose) => (
        <span>
          <Tag color={record.color} style={{ marginRight: 8, width: 12, height: 12, display: 'inline-block', borderRadius: '50%', padding: 0, border: 'none' }} />
          {record.categoria}
        </span>
      ),
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'center' as const,
      render: (v: number) => <strong>{v.toLocaleString('es-MX')}</strong>,
    },
    {
      title: '% del total',
      key: 'pct',
      align: 'center' as const,
      render: (_: unknown, record: FilaDesglose) => {
        const pct = total > 0 ? Math.round((record.cantidad / total) * 1000) / 10 : 0;
        return <Text>{pct}%</Text>;
      },
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={720}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>Reglas de Conciliación</span>
          <Tag style={{ fontFamily: 'monospace' }}>{rfc}</Tag>
        </div>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="Periodo">{periodo || '—'}</Descriptions.Item>
          <Descriptions.Item label="Total movimientos">{total.toLocaleString('es-MX')}</Descriptions.Item>
        </Descriptions>
        <div style={{ marginTop: 8 }}>
          <Progress
            percent={Math.min(porcentaje, 100)}
            size="default"
            strokeColor={pctColor(porcentaje)}
            format={() => `${Math.round(porcentaje)}% conciliado`}
          />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Text
          strong
          style={{
            cursor: 'pointer',
            color: activeTab === 'desglose' ? '#1890ff' : '#595959',
            marginRight: 24,
            borderBottom: activeTab === 'desglose' ? '2px solid #1890ff' : 'none',
            paddingBottom: 4,
          }}
          onClick={() => setActiveTab('desglose')}
        >
          Desglose de esta conciliación
        </Text>
        <Text
          strong
          style={{
            cursor: 'pointer',
            color: activeTab === 'reglas' ? '#1890ff' : '#595959',
            borderBottom: activeTab === 'reglas' ? '2px solid #1890ff' : 'none',
            paddingBottom: 4,
          }}
          onClick={() => setActiveTab('reglas')}
        >
          Cómo funciona el scoring
        </Text>
      </div>

      {activeTab === 'desglose' ? (
        <Table
          dataSource={filas}
          columns={columns}
          pagination={false}
          size="small"
          rowKey="key"
          summary={() => (
            <Table.Summary>
              <Table.Summary.Row style={{ fontWeight: 700, background: '#f6ffed' }}>
                <Table.Summary.Cell index={0}>Total movimientos</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="center">{total.toLocaleString('es-MX')}</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="center">100%</Table.Summary.Cell>
                <Table.Summary.Cell index={3} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      ) : (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            El sistema asigna puntos a cada candidato CFDI para un movimiento bancario.
            Si el score es {'>= 80'}, se concilia automáticamente.
          </Text>
          <Table
            dataSource={REGLAS_SCORING.map((r, i) => ({ ...r, key: i }))}
            columns={[
              { title: 'Regla', dataIndex: 'regla', key: 'regla' },
              {
                title: 'Puntos',
                dataIndex: 'puntos',
                key: 'puntos',
                align: 'center' as const,
                render: (v: string | number) => (
                  <Tag color={typeof v === 'number' && v < 0 ? 'red' : v === 100 ? 'blue' : 'green'}>
                    {v}
                  </Tag>
                ),
              },
            ]}
            pagination={false}
            size="small"
            rowKey="key"
          />
          <Divider />
          <Title level={5}>Niveles de conciliación</Title>
          <ul style={{ paddingLeft: 20, color: '#595959' }}>
            <li><strong>100 pts:</strong> Clave de rastreo SPEI exacta (conciliación definitiva)</li>
            <li><strong>80 - 99 pts:</strong> Conciliación automática por scoring combinado</li>
            <li><strong>&lt; 80 pts:</strong> Sin conciliación automática; se muestran top 3 sugerencias</li>
            <li><strong>IA (Nova Lite):</strong> Segunda pasada sobre transferencias sin conciliación previa</li>
          </ul>
        </div>
      )}
    </Modal>
  );
}
